// Spaced repetition: Leitner-style steps with fixed intervals, keyed by word id.
// Pure functions only — App.js owns the React state and persistence timing.
//
// A word climbs one step per correct spelling and falls back to step 0 when
// missed or skipped. Step 0 also means "learning this session": the word is
// re-asked after a few other words and must be spelled right once before it
// earns a real interval. "Mastered" is just a long interval, not a flag, so a
// word forgotten in two months still comes back.

export const STORAGE_KEY = 'progress';
const DAY = 24 * 60 * 60 * 1000;
export const INTERVALS_DAYS = [0, 1, 3, 7, 14, 30, 60];
export const MASTERED_STEP = INTERVALS_DAYS.length - 1;
export const RELEARN_GAP = 5;   // other words asked before a missed word returns
export const NEW_EVERY = 3;     // introduce one new word per this many picks when reviews are waiting

export const emptyProgress = () => ({ v: 1, words: {} });

export const loadProgress = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (stored && stored.v === 1 && stored.words && typeof stored.words === 'object') return stored;
  } catch (_) {}
  return emptyProgress();
};

export const saveProgress = (progress) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(progress)); } catch (_) {}
};

// ±15% jitter so words learned together do not all fall due on the same day.
const jitter = (ms, random) => Math.round(ms * (0.85 + 0.3 * random()));

// Returns a new progress object with the result of one graded attempt applied.
export const record = (progress, id, correct, now = Date.now(), random = Math.random) => {
  const prev = progress.words[id] || { step: 0, due: 0, lapses: 0, seen: 0 };
  const step = correct ? Math.min(prev.step + 1, MASTERED_STEP) : 0;
  const due = correct ? now + jitter(INTERVALS_DAYS[step] * DAY, random) : now;
  const entry = { step, due, lapses: prev.lapses + (correct ? 0 : 1), seen: prev.seen + 1 };
  return { ...progress, words: { ...progress.words, [id]: entry } };
};

export const isMastered = (entry) => !!entry && entry.step >= MASTERED_STEP;
export const isDue = (entry, now) => !!entry && entry.due <= now;

// Counts for the home screen: how much of `words` is new, due now, or mastered.
export const summarize = (words, progress, now = Date.now()) => {
  const summary = { total: words.length, fresh: 0, due: 0, mastered: 0, learning: 0 };
  words.forEach(({ id }) => {
    const entry = progress.words[id];
    if (!entry) summary.fresh += 1;
    else if (isMastered(entry)) summary.mastered += 1;
    else summary.learning += 1;
    if (isDue(entry, now)) summary.due += 1; // mastered words still come due eventually
  });
  return summary;
};

// Session bookkeeping is a plain object App.js keeps in a ref:
//   asked:    ids asked this session, in order
//   learning: ids missed this session and not yet re-spelled correctly
export const newSession = () => ({ asked: [], learning: [] });

const pickRandom = (items, random) => items[Math.floor(random() * items.length)];

// Chooses the next word. `mode` is 'spaced' (default) or 'everything' (a mock bee:
// uniform random across the list, still graded). Returns { word, reason } where
// reason ∈ learning | due | new | ahead | random, or null when the list is empty.
export const pickNext = (words, progress, session, { now = Date.now(), random = Math.random, mode = 'spaced' } = {}) => {
  if (!words.length) return null;
  const byId = new Map(words.map(w => [String(w.id), w]));
  const last = session.asked[session.asked.length - 1];
  const notLast = (w) => words.length === 1 || String(w.id) !== String(last);

  if (mode === 'everything') {
    const pool = words.filter(notLast);
    return { word: pickRandom(pool, random), reason: 'random' };
  }

  // 1. A word missed this session, once enough other words have gone by.
  const recent = new Set(session.asked.slice(-RELEARN_GAP).map(String));
  const relearn = session.learning.map(String).filter(id => byId.has(id) && !recent.has(id));
  if (relearn.length) return { word: byId.get(relearn[0]), reason: 'learning' };

  const seenThisSession = new Set(session.asked.map(String));
  const untouched = words.filter(w => !seenThisSession.has(String(w.id)));
  const due = untouched.filter(w => progress.words[w.id] && isDue(progress.words[w.id], now));
  const fresh = untouched.filter(w => !progress.words[w.id]);

  // 2/3. Reviews first, but let a new word through every NEW_EVERY picks so a heavy
  // review day still introduces vocabulary.
  const picks = session.asked.length;
  const wantNew = fresh.length && (!due.length || picks % NEW_EVERY === NEW_EVERY - 1);
  if (wantNew) return { word: pickRandom(fresh, random), reason: 'new' };
  if (due.length) {
    const overdue = Math.min(...due.map(w => progress.words[w.id].due));
    const most = due.filter(w => progress.words[w.id].due === overdue);
    return { word: pickRandom(most, random), reason: 'due' };
  }

  // 4. Nothing due, nothing new: review ahead of schedule, soonest due first.
  const ahead = (untouched.length ? untouched : words.filter(notLast))
    .sort((a, b) => (progress.words[a.id]?.due || 0) - (progress.words[b.id]?.due || 0));
  return { word: ahead[0], reason: 'ahead' };
};

// Apply a graded attempt to the session record.
export const noteResult = (session, id, correct) => {
  const learning = session.learning.filter(x => String(x) !== String(id));
  return { asked: session.asked, learning: correct ? learning : [...learning, id] };
};

export const noteAsked = (session, id) => ({ ...session, asked: [...session.asked, id] });

// Drop progress for the given ids (or everything when ids is null).
export const resetProgress = (progress, ids = null) => {
  if (!ids) return emptyProgress();
  const drop = new Set(ids.map(String));
  const words = Object.fromEntries(Object.entries(progress.words).filter(([id]) => !drop.has(id)));
  return { ...progress, words };
};
